import { useEffect, useState } from "react";
import { Button } from "./Button";
import { Modal } from "./Modal";

const CONSENT_STORAGE_KEY = "cookieConsent";

type ConsentPreferences = {
  essential: true; // toujours vrai
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
};

const getStoredConsent = (): ConsentPreferences | null => {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed === "object" && parsed)
      return parsed as ConsentPreferences;
    return null;
  } catch {
    return null;
  }
};

const saveConsent = (prefs: ConsentPreferences) => {
  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(prefs));
};

export const CookieConsent = () => {
  const [prefs, setPrefs] = useState<ConsentPreferences | null>(null);
  const [isPrefsOpen, setIsPrefsOpen] = useState(false);
  const [draftAnalytics, setDraftAnalytics] = useState(true);
  const [draftMarketing, setDraftMarketing] = useState(false);

  useEffect(() => {
    const stored = getStoredConsent();
    if (stored) {
      setPrefs(stored);
    }
  }, []);

  const acceptAll = () => {
    const decision: ConsentPreferences = {
      essential: true,
      analytics: true,
      marketing: true,
      timestamp: Date.now(),
    };
    saveConsent(decision);
    setPrefs(decision);
  };

  const refuseAll = () => {
    const decision: ConsentPreferences = {
      essential: true,
      analytics: false,
      marketing: false,
      timestamp: Date.now(),
    };
    saveConsent(decision);
    setPrefs(decision);
  };

  const openCustomize = () => {
    // Valeurs par défaut quand on ouvre
    const stored = getStoredConsent();
    setDraftAnalytics(stored?.analytics ?? true);
    setDraftMarketing(stored?.marketing ?? false);
    setIsPrefsOpen(true);
  };

  const saveCustomize = () => {
    const decision: ConsentPreferences = {
      essential: true,
      analytics: draftAnalytics,
      marketing: draftMarketing,
      timestamp: Date.now(),
    };
    saveConsent(decision);
    setPrefs(decision);
    setIsPrefsOpen(false);
  };

  // Si consentement déjà donné, ne rien afficher
  if (prefs) return null;

  return (
    <>
      <div className="fixed inset-x-0 bottom-0 z-40">
        <div className="mx-auto max-w-4xl m-4 rounded-lg shadow-xl border border-grey-light bg-white bg-opacity-70">
          <div className="p-4 md:p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="text-sm md:text-base text-default-text leading-relaxed">
              Nous utilisons des cookies essentiels pour le fonctionnement du
              site et, avec votre accord, des cookies de mesure d'audience et
              marketing. Vous pouvez accepter, refuser ou personnaliser vos
              choix. Consultez notre politique pour en savoir plus.
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <Button
                type="button"
                variant="secondary"
                size="small"
                onClick={refuseAll}
              >
                Tout refuser
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="small"
                onClick={openCustomize}
              >
                Personnaliser
              </Button>
              <Button type="button" size="small" onClick={acceptAll}>
                Tout accepter
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Préférences */}
      <Modal
        isOpen={isPrefsOpen}
        onClose={() => setIsPrefsOpen(false)}
        title="Préférences de cookies"
        icon="info-circle"
        maxWidth="md"
      >
        <div className="space-y-4">
          <div className="p-3 bg-grey-light rounded-lg">
            <div className="font-oxaniumMedium text-tertiary">Essentiels</div>
            <p className="text-sm text-default-text">
              Toujours actifs. Indispensables au bon fonctionnement du site.
            </p>
          </div>

          <label className="flex items-start gap-3 p-3 rounded-lg border border-grey-light cursor-pointer">
            <input
              type="checkbox"
              className="mt-1"
              checked={draftAnalytics}
              onChange={(e) => setDraftAnalytics(e.target.checked)}
            />
            <div>
              <div className="font-oxaniumMedium text-tertiary">
                Mesure d'audience
              </div>
              <p className="text-sm text-default-text">
                Nous aide à comprendre l'utilisation du site pour l'améliorer.
              </p>
            </div>
          </label>

          <label className="flex items-start gap-3 p-3 rounded-lg border border-grey-light cursor-pointer">
            <input
              type="checkbox"
              className="mt-1"
              checked={draftMarketing}
              onChange={(e) => setDraftMarketing(e.target.checked)}
            />
            <div>
              <div className="font-oxaniumMedium text-tertiary">Marketing</div>
              <p className="text-sm text-default-text">
                Permet l'affichage de contenus personnalisés et de publicités
                pertinentes.
              </p>
            </div>
          </label>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="secondary"
              size="small"
              onClick={() => setIsPrefsOpen(false)}
            >
              Annuler
            </Button>
            <Button type="button" size="small" onClick={saveCustomize}>
              Enregistrer mes choix
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
